import moment from 'moment';
import harvestPlanApi from '@/api/harvestPlanApi';
import * as Constants from "@/helpers/constants";
import UtilService from '@/services/utilService.js';
import _ from 'lodash';

const getDefaultState = () => {
    return {
        error: null,
        selectedDate: moment().add(1, 'day').format('YYYY-MM-DD'),        
        loadingPlants: false,        
        loadingHarvestPlan: false,
        updatingHarvestPlan: false,
        plantData: null,
        selectedPlantID: null,
        loadingWeatherData: false,
        weatherData: null,
        loadingPlantUnits: false,
        plantUnits: null,
        loadingSettings: false,
        settings: null,
        selectedCategoryIDs: null,
        harvestPlan: null,
        sendingAgreements: false,
        agreementSendResult: {
            success: false,
            display: false,
            message: null
        },
        harvestPlanLocalStack:[]
    }
}

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        selectedUOM(state) {
            if(state.harvestPlan == null || state.harvestPlan.HarvestPlanUOMs == null || state.selectedCategoryIDs == null) {
                return null;
            }

            let selection = [];

            if (!Array.isArray(state.selectedCategoryIDs)) {
                selection.push(state.selectedCategoryIDs);
            } else {
                selection = state.selectedCategoryIDs;
            }

            let uoms = state.harvestPlan.HarvestPlanUOMs.filter(u => selection.some(c => c == u.ProductCategoryID));
            let selectedUOM = null;

            if (uoms.length > 0 && uoms.every(u => u.UOM == uoms[0].UOM)) {
                selectedUOM = uoms[0].UOM;
            }

            return selectedUOM;
        },
        startOfSelectedWeek(state){
            return moment(state.selectedDate)
                .startOf('week')
                .format('YYYY-MM-DD');
        },
        endOfSixWeeksFromSelected(state){
            return moment(state.selectedDate)
                .add(6, 'weeks')
                .endOf('week')
                .format('YYYY-MM-DD')
        },
        isPast(state) {
            return moment(state.selectedDate).toDate().setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);
        },
        getTeams(state, getters) {
            return state.harvestPlan.Teams.filter(t => getters.getTeamDetails(t.BerriesHarvestPlanTeamID).length > 0).sort(UtilService.sortAscending(t => t.TeamName))
        },
        getTeamDetails(state) {
            return (berriesHarvestPlanTeamID) => {
                let details = []
                
                let teams = state.harvestPlan.Teams.filter(t => t.BerriesHarvestPlanTeamID == (berriesHarvestPlanTeamID || t.BerriesHarvestPlanTeamID));                
                teams.forEach(team => {
                    details = details.concat(team.Details);
                })                

                if (state.selectedCategoryIDs && state.selectedCategoryIDs.length > 0) {
                    details = details.filter(d => state.selectedCategoryIDs.some(c => c == d.ProductCategoryID));
                }

                var sortedDetails = details.sort(UtilService.sortAscending(d => (d.CropLocationSortOrder * 10) + d.SortOrder));
                var lastDetail = null;
                for(var i = sortedDetails.length-1; i >=0 ; i --) {
                    var detail = sortedDetails[i];
                    
                    if(lastDetail) {
                        detail.nextBerriesHarvestPlanTeamDetailID = lastDetail.BerriesHarvestPlanTeamDetailID;
                    } else {
                        detail.nextBerriesHarvestPlanTeamDetailID = null;
                    }
                    
                    lastDetail = detail;
                }
                lastDetail = null;
                return sortedDetails;
            }
        },
        getTeamVolume(state, getters) {
            return (berriesHarvestPlanTeamID) => {
                let volumes = getters.getTeamDetails(berriesHarvestPlanTeamID).map(d => d.VolumeUOM);

                if (volumes && volumes.length > 0) {
                    return volumes.reduce((a, b) => a + b);
                }
                
                return 0;
            }
        },
        getTeamICAVolume(state, getters) {
            return (berriesHarvestPlanTeamID) => {
                let volumes = getters.getTeamDetails(berriesHarvestPlanTeamID).filter(d => d.NuPointSprays.some(s => s.ICAProtected)).map(d => d.VolumeUOM);

                if (volumes && volumes.length > 0) {
                    return volumes.reduce((a, b) => a + b);
                }
                
                return 0;
            }
        },
        getTeamStartTime(state) {
            return (berriesHarvestPlanTeamID) => {
                let team = state.harvestPlan.Teams.find(t => t.BerriesHarvestPlanTeamID == berriesHarvestPlanTeamID);

                if (team.StartTime) {
                    let harvestPlanDate = moment(state.harvestPlan.HarvestPlanDate);
                    let startTime = moment(team.StartTime, moment.HTML5_FMT.TIME);
                    return moment({ year: harvestPlanDate.year(), month: harvestPlanDate.month(), day: harvestPlanDate.date(), hour: startTime.hour(), minute: startTime.minute() })
                }

                return null;
            }
        },
        getTeamEstimatedFinishTime(state, getters){
            return (berriesHarvestPlanTeamID) => {
                let team = state.harvestPlan.Teams.find(t => t.BerriesHarvestPlanTeamID == berriesHarvestPlanTeamID);
                // We must have a start time to calculate and end time
                if (!team.StartTime) {
                    return null;
                }

                // Otherwise lets calculate it.
                let duration = 0;

                let details = getters.getTeamDetails(berriesHarvestPlanTeamID);
                details.forEach(detail => {
                    duration += detail.TimeInLocationMinutes || 0;
                });

                // Unique Crop Locations
                let uniqueCropLocations = details.map(d => { return d.CropLocationID }).filter((item, i, ar) => ar.indexOf(item) === i).length;
                duration += (uniqueCropLocations - 1) * state.settings.MoveDuration;

                // If the end time extends past 3 hours, add a paid break
                if (duration > 180) {
                    duration += state.settings.PaidBreakDuration;
                }

                // If the end time extends past 5.5 hours, add an unpaid break
                if (duration > 330) {
                    duration += state.settings.UnpaidBreakDuration;
                }
                return getters.getTeamStartTime(berriesHarvestPlanTeamID).add(duration, 'minutes');
            }
        },
        getTeamWrittenFinishTime(state){
            return (berriesHarvestPlanTeamID) => {
                let team = state.harvestPlan.Teams.find(t => t.BerriesHarvestPlanTeamID == berriesHarvestPlanTeamID);

                // Return the end time if it was specifically set by the user
                if (team.EndTime) {
                    let endTime = moment(team.EndTime, moment.HTML5_FMT.TIME);
                    let startTime = moment(team.StartTime || team.EndTime, moment.HTML5_FMT.TIME);
                    let harvestPlanDate = moment(state.harvestPlan.HarvestPlanDate);                    

                    if (endTime.isBefore(startTime)) {
                        let nextDay = harvestPlanDate.add(1, 'days');
                        return moment({ year: nextDay.year(), month: nextDay.month(), day: nextDay.date(), hour: endTime.hour(), minute: endTime.minute() })
                    } else {                        
                        return moment({ year: harvestPlanDate.year(), month: harvestPlanDate.month(), day: harvestPlanDate.date(), hour: endTime.hour(), minute: endTime.minute() })
                    }
                }
                return null;
            }
        },
        getTeamFinishTime(state, getters) {
            return (berriesHarvestPlanTeamID) => {
                let written = getters.getTeamWrittenFinishTime(berriesHarvestPlanTeamID)
                if(written == null){
                    return getters.getTeamEstimatedFinishTime(berriesHarvestPlanTeamID)
                }
                return written;
            }
        },
        getTeamVolumeKg(state, getters) {
            return (berriesHarvestPlanTeamID) => {
                let volumes = getters.getTeamDetails(berriesHarvestPlanTeamID).map(d => d.VolumeKg);

                if (volumes && volumes.length > 0) {
                    return volumes.reduce((a, b) => a + b);
                }
                
                return 0;
            }
        },
        getTeamPickRate(state, getters) {
            return (berriesHarvestPlanTeamID) => {
                let totalVolume = getters.getTeamVolume(berriesHarvestPlanTeamID);

                if (totalVolume && totalVolume > 0) {
                    let details = getters.getTeamDetails(berriesHarvestPlanTeamID);

                    let weightedPickRate = 0;                    

                    details.forEach(detail => {                        
                        let weight = (detail.VolumeUOM || 0) / totalVolume;                        
                        weightedPickRate += (detail.PickRateUOM || 0) * weight;
                    });

                    // 1 decimal place
                    return Math.round(weightedPickRate * 10) / 10;
                }
                
                return 0;
            }
        },
        getTeamPieceRate(state, getters) {
            return (berriesHarvestPlanTeamID) => {
                let details = getters.getTeamDetails(berriesHarvestPlanTeamID);

                let totalVolume = 0;
                details.forEach(detail => {
                    if (detail.PayStyle == Constants.HARVESTPLAN_PAYSTYLE_ENUM.PIECERATE.ID) {
                        totalVolume += detail.VolumeUOM;
                    }
                });
                
                if (totalVolume > 0) {
                    let weightedPayRate = 0;                    

                    details.forEach(detail => {
                        if (detail.PayStyle == Constants.HARVESTPLAN_PAYSTYLE_ENUM.PIECERATE.ID) {
                            let weight = (detail.VolumeUOM || 0) / totalVolume;
                            weightedPayRate += (detail.OverriddenPayRate || detail.PayRate || 0) * weight;
                        }
                    });

                    // 2 decimal places
                    return Math.round(weightedPayRate * 100) / 100;
                }
                
                return 'N/A';
            }
        },
        getTeamCost(state, getters) {
            return (berriesHarvestPlanTeamID) => {
                let teams = state.harvestPlan.Teams.filter(t => t.BerriesHarvestPlanTeamID == (berriesHarvestPlanTeamID || t.BerriesHarvestPlanTeamID));

                let totalCost = 0;

                teams.forEach(team => {
                    let endTime = getters.getTeamEstimatedFinishTime(team.BerriesHarvestPlanTeamID);

                    if (team.StartTime && endTime) {
                        // Calculate Overhead Cost
                        let workHours = moment(endTime.format('HH:mm'), moment.HTML5_FMT.TIME).diff(moment(team.StartTime, moment.HTML5_FMT.TIME), 'minutes') / 60;                        
                        let overheadCost = workHours * team.Assistants * state.settings.AssistantPayRate;

                        // Supervisors
                        if (team.PickerStaff + team.Assistants >= 50 && team.Supervisors == 1) {
                            overheadCost += workHours * team.Supervisors * state.settings.SuperisorPayRateInclAllowance;
                        } else {
                            overheadCost += workHours * team.Supervisors * state.settings.SupervisorPayRate;
                        }

                        // Calculate Picker Cost
                        let details = getters.getTeamDetails(team.BerriesHarvestPlanTeamID);
                        
                        let blockCost = 0;
                        details.forEach(detail => {                            
                            if (detail.PayStyle == Constants.HARVESTPLAN_PAYSTYLE_ENUM.HOURLY.ID) {
                                blockCost += Math.round((detail.TimeInLocationMinutes / 60) * (detail.OverriddenPayRate || detail.PayRate) * team.PickerStaff * 100) / 100;
                            }

                            if (detail.PayStyle == Constants.HARVESTPLAN_PAYSTYLE_ENUM.PIECERATE.ID) {
                                blockCost += Math.round((detail.VolumeUOM * detail.UnitsInTray) * (detail.OverriddenPayRate || detail.PayRate) * 100) / 100;
                            }
                        });

                        // Unique Crop Locations
                        let uniqueCropLocations = details.map(d => { return d.CropLocationID }).filter((item, i, ar) => ar.indexOf(item) === i).length;

                        // 10 Minutes per location
                        let moveCost = (state.settings.MoveDuration / 60) * (uniqueCropLocations - 1) * state.settings.PickerPayRate * team.PickerStaff;

                        // add a paid break if work time is greater than 3 hours
                        let breakCost = 0;
                        if(workHours > 3) {
                            breakCost = (state.settings.PaidBreakDuration / 60) * state.settings.PickerPayRate * team.PickerStaff;
                        }

                        totalCost += overheadCost + blockCost + moveCost + breakCost;
                    }
                });

                return Math.round(totalCost * 100) / 100 || 'N/A';
            }
        },
        canRevertHarvestPlan(state) {
            return state.harvestPlanLocalStack.length > 1;
        }
    },
    mutations: {
        setSelectedPlantID(state, selectedPlantID){
            state.selectedPlantID = selectedPlantID;
        },
        setSelectedCategoryIDs(state, selectedCategoryIDs){
            state.selectedCategoryIDs = selectedCategoryIDs;
        },
        setSelectedDate(state, selectedDate) {
            state.selectedDate = selectedDate;
        },
        setSeletedUOM(state, selectedUOM) {
            if (state.selectedCategoryIDs && state.harvestPlan) {
                state.selectedCategoryIDs.forEach(category => {
                    let uom = state.harvestPlan.HarvestPlanUOMs.find(u => u.ProductCategoryID == category);
                    uom.UOM = selectedUOM;
                });
            }
        },
        addTeamDetail(state, teamDetail) {
            let team = state.harvestPlan.Teams.find(t => t.BerriesHarvestPlanTeamID == teamDetail.BerriesHarvestPlanTeamID);

            if (team) {
                team.Details.push(teamDetail);
            }
        },
        pushHarvestPlan(state, harvestPlan) {
            // harvestPlanLocalStack is a deque
            // the stackSize is the number of most recent changes we keep
            // if in future the customer wants to revert backward at most k times, just change the stackSize to k
            let stackSize = 1;
            while (state.harvestPlanLocalStack.length > stackSize) {
                state.harvestPlanLocalStack.shift();
            }
            state.harvestPlanLocalStack.push(_.cloneDeep(harvestPlan));
        },
        // the state will always have the lastest version on top of the stack due to the current implementation
        // e.g. if stack have [1,2,3] then 3 is the current verion, 2 is the last modify version and 1 is the version before last modify version
        initHarvestPlanLocalStack(state) {
            state.harvestPlanLocalStack = [_.cloneDeep(state.harvestPlan)];
        }
    },
    actions: {
        async resetState({ state }){
            Object.assign(state, getDefaultState())
        },
        async loadPlants({ state }){
            state.loadingPlants = true;
            state.error = null;
            let plantData = [];

            try {
                plantData = await harvestPlanApi.getPlantData();
                if(plantData == null){
                    plantData = [];
                }
            } catch(e) {                
                state.error = e.toString();
            }

            state.plantData = plantData;
            state.loadingPlants = false;
        },
        async loadWeatherData({ state, getters }){
            state.loadingWeatherData = true;
            state.error = null;

            try {
                state.weatherData = await harvestPlanApi.getWeatherData(
                    state.selectedPlantID,
                    getters.startOfSelectedWeek,
                    getters.endOfSixWeeksFromSelected
                );
            } catch(e) {                
                state.error = e.toString();
            }

            state.loadingWeatherData = false;
        },
        async loadPlantUnits({ state }) {
            state.loadingPlantUnits = true;
            state.error = null;

            try {
                state.plantUnits = await harvestPlanApi.getPlantUnits(state.selectedPlantID);
            } catch(e) {                
                state.error = e.toString();
            }

            state.loadingPlantUnits = false
        },
        async loadSettings({ state }) {
            state.loadingSettings = true;
            state.error = null;

            try {
                state.settings = await harvestPlanApi.getSettings(state.selectedPlantID, state.selectedDate)
            } catch(e) {                
                state.error = e.toString();
            }

            state.loadingSettings = false;
        },
        async loadHarvestPlan({ state, commit }) {
            state.loadingHarvestPlan = true;
            state.error = null;

            try {
                state.harvestPlan = await harvestPlanApi.getHarvestPlan(
                    state.selectedPlantID,                
                    state.selectedDate
                );
                commit('initHarvestPlanLocalStack');
            } catch(e) {                
                state.error = e.toString();
            }

            state.loadingHarvestPlan = false;
        },
        async updateHarvestPlanDetail({ state, commit }) {    
            state.updatingHarvestPlan = true;
            state.error = null;

            try {
                state.harvestPlan.UpdateDetails = true;
                state.harvestPlan = await harvestPlanApi.updateHarvestPlan(
                    state.harvestPlan
                );
                commit('pushHarvestPlan', state.harvestPlan);
            } catch(e) {                
                state.error = e.toString();
            }

            state.updatingHarvestPlan = false;
        },
        async updateHarvestPlanUOM({ state, commit}) {    
            state.updatingHarvestPlan = true;
            state.error = null;

            try {
                state.harvestPlan.UpdateUOM = true;
                state.harvestPlan = await harvestPlanApi.updateHarvestPlan(
                    state.harvestPlan
                );
                commit('pushHarvestPlan', state.harvestPlan);
            } catch(e) {
                state.error = e.toString();
            }

            state.updatingHarvestPlan = false;
        },
        async updateHarvestPlanTeam({ state, commit }, berriesHarvestPlanTeamID) {
            state.updatingHarvestPlan = true;
            state.error = null;

            try {
                state.harvestPlan.UpdateTeams = true;
                let plan = JSON.parse(JSON.stringify(state.harvestPlan));

                // Filter the team we wish to update (if no valid PlanTeamID provided, update all teams)
                if(berriesHarvestPlanTeamID != -1){
                    plan.Teams = plan.Teams.filter(t => t.BerriesHarvestPlanTeamID == berriesHarvestPlanTeamID);
                }
                state.harvestPlan = await harvestPlanApi.updateHarvestPlan(
                    plan
                );
                commit('pushHarvestPlan', state.harvestPlan);
            } catch(e) {                
                state.error = e.toString();
            }

            state.updatingHarvestPlan = false;
        },
        async updateTeamPayStyle({ state }, payload){
            
            let team = state.harvestPlan.Teams.find(t => t.BerriesHarvestPlanTeamID == (payload.harvestTeamID));
            if (team == undefined) {
                state.error = "Unable to bulk update Pay Style for Team"
            }
            else {
                var details = team.Details.sort((a, b) => {
                    if (a.CropLocationSortOrder - b.CropLocationSortOrder == 0) {
                        return a.SortOrder - b.SortOrder
                    }
                    return a.CropLocationSortOrder - b.CropLocationSortOrder
                })

                for (let i = 0; i < details.length; i++) {
                    var teamDetail = details[i]
                    if (teamDetail.PayStyle == null) {
                        teamDetail.PayStyle = payload.payStyle
                    }
                }
            }

        },
        async updateTeamStartTime({ getters }, harvestPlanTeam) {
            if (harvestPlanTeam.StartTimeEdit !== undefined ) {                
                harvestPlanTeam.StartTime = harvestPlanTeam.StartTimeEdit;

                getters.getTeams.forEach(t => {
                    if (t.StartTime == null || t.StartTime == undefined) {
                        t.StartTime = harvestPlanTeam.StartTime;
                    }
                });
            }
        },
        async sendSelectedTeamsPieceRateAgreement({ state }){
            try {
                //get the teams we have selected to email piece rates too
                let teams = state.harvestPlan.Teams.filter(t => t.SendPieceRateAgreement).map(t => t.TeamID);
                state.sendingAgreements = true;
                state.agreementSendResult.display = false

                //check our team/s have start time
                let teamWithStart = state.harvestPlan.Teams.filter(t => t.SendPieceRateAgreement && t.StartTime != null)
                if(teamWithStart.length != teams.length) {
                    throw new Error("Start time cannot be NULL")
                }

                await harvestPlanApi.sendPieceRateAgreements(state.selectedPlantID,
                    state.selectedDate,
                    teams
                );
                state.agreementSendResult.display = true;
                state.agreementSendResult.success = true;
                state.agreementSendResult.message = 'Piecerate Agreement emails queued';
                
                state.harvestPlan = await harvestPlanApi.updateHarvestPlan(
                    state.harvestPlan
                );
            } catch(e) {
                state.agreementSendResult.display = true;
                state.agreementSendResult.success = false;
                state.agreementSendResult.message = e.toString();
                console.log(e)
            }

            state.sendingAgreements = false;
        },
        dismissPiecerateAgreementNotification({ state }) {
            state.agreementSendResult.success = false;
            state.agreementSendResult.display = false
            state.agreementSendResult.message = null;
        },
        async revertLastChange({ state }) {
            state.updatingHarvestPlan = true;

            state.harvestPlanLocalStack.pop();
            let lastVersion = state.harvestPlanLocalStack[state.harvestPlanLocalStack.length - 1];

            if (lastVersion) {
                lastVersion.UpdateTeams = true;
                lastVersion.UpdateDetails = true;
                lastVersion.UpdateUOM = true;
                state.harvestPlan = await harvestPlanApi.updateHarvestPlan(
                    lastVersion
                );
            }
            state.updatingHarvestPlan = false;
        }
    }
}